import type { LucideIcon } from 'lucide-react'
import { MoreHorizontal } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useIsMobile } from '@/hooks/use-mobile'

/**
 * One control in a schema header's action cluster.
 *
 * A plain action needs only `label`, `icon` and `onSelect` — it becomes a button
 * on the heading row and a menu item once the header collapses. The two node
 * slots cover the controls that are not plain buttons (a version select, a
 * nested menu), so a control is still declared once and the behaviour behind it
 * never has to be repeated per presentation.
 */
export type SchemaHeaderAction = {
  id: string
  label: string
  icon: LucideIcon
  onSelect?: () => void
  disabled?: boolean
  /** Long-form explanation, used as the button's tooltip. */
  hint?: string
  /** Tint the menu item as destructive. */
  destructive?: boolean
  /** Keep this one in the overflow menu even while the cluster is expanded —
   * for lifecycle actions that never earned a button of their own. */
  inMenu?: boolean
  /** Replaces the default button on the heading row. */
  expanded?: ReactNode
  /** Replaces the default item in the overflow menu. */
  collapsed?: ReactNode
}

/**
 * A schema header's action cluster, in whichever form currently fits: buttons
 * beside the schema name, or — when {@link SchemaHero} reports they no longer
 * fit — the same actions inside the one overflow menu. There is never more than
 * one Actions menu in the header: it holds the `inMenu` actions while there is
 * room, and everything folds into it when there isn't.
 */
export function SchemaHeaderActions({
  actions,
  collapsed,
}: {
  actions: SchemaHeaderAction[]
  collapsed: boolean
}) {
  const { t } = useTranslation()
  const inline = collapsed ? [] : actions.filter(action => !action.inMenu)
  const menu = collapsed ? actions : actions.filter(action => action.inMenu)
  return (
    <>
      {menu.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Below md an icon-only square, leaving the row to the schema name. */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 max-md:size-9 max-md:px-0 border-border"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="max-md:sr-only">{t('schemaAgent.header.actions')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 border-border bg-card">
            {menu.map((action, index) => (
              <Fragment key={action.id}>
                {/* Separate management actions from controls folded in on narrow screens. */}
                {index > 0 && Boolean(action.inMenu) !== Boolean(menu[index - 1].inMenu) && (
                  <DropdownMenuSeparator />
                )}
                {action.collapsed ?? <ActionItem action={action} />}
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {inline.map(action => (
        <Fragment key={action.id}>{action.expanded ?? <ActionButton action={action} />}</Fragment>
      ))}
    </>
  )
}

/**
 * A nested menu for an action's `collapsed` slot. A phone has no room beside
 * the overflow menu for a flyout (it opens half off-screen), so there the
 * choices are listed in place under the label.
 */
export function SchemaHeaderSubMenu({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon
  label: string
  children: ReactNode
}) {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {children}
      </>
    )
  }
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Icon className="mr-2 h-4 w-4" />
        {label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="border-border bg-card">{children}</DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

function ActionButton({ action }: { action: SchemaHeaderAction }) {
  const Icon = action.icon
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={action.onSelect}
      disabled={action.disabled}
      title={action.hint}
      className="h-8 border-border"
    >
      <Icon className="mr-1 h-4 w-4" />
      {action.label}
    </Button>
  )
}

function ActionItem({ action }: { action: SchemaHeaderAction }) {
  const Icon = action.icon
  return (
    <DropdownMenuItem
      onClick={action.onSelect}
      disabled={action.disabled}
      className={action.destructive ? 'text-destructive focus:text-destructive' : undefined}
    >
      <Icon className="mr-2 h-4 w-4" />
      {action.label}
    </DropdownMenuItem>
  )
}
